// Render-time geometry corrections for docx-preview native-page-mode output. Applied to the LIVE
// DOM in BOTH places the template HTML is shown — the wizard preview (TemplateUploadStep) and the
// PDF capture host (htmlToPdf) — so the two stay pixel-identical (WYSIWYG) and both match Word.
//
// WHY RENDER-TIME (not baked at convert time): the corrections are geometric (they re-center wide
// drawings on the page), which requires measuring the element's laid-out box. docx-preview positions
// full-bleed header/footer bars with document-specific negative indents whose resolved side depends
// on the paragraph's bidi direction; the SAME element can land at a different x under a different
// base direction. Measuring in the actual render context sidesteps that entirely — whatever the base
// position is, we center it — whereas a pixel offset computed in a different context lands wrong.
//
// Two Word behaviours docx-preview doesn't reproduce, both keyed off its own structural output so
// they generalize to any uploaded .docx (never hardcoded to one document):
//  (a) Behind-text WATERMARKS (drawings docx-preview parks on a large negative z-index). Word centers
//      these via mso-position:center; docx-preview leaves them at the containing-block origin. We
//      honor the mso-position hints and center on the margin box.
//  (b) Full-bleed HEADER/FOOTER bars (letterheads, contact bars wider than the text column). Word
//      anchors them centered on the PAGE and lets them bleed into the horizontal margins; docx-preview
//      mis-anchors some (a footer can land ~250px too far right and clip). We re-center them on the page.

export function applyDocxLayoutFixes(root: HTMLElement | null | undefined): void {
  if (!root) return;
  const sections = root.querySelectorAll('section.docx');
  sections.forEach((sectionNode) => {
    const sec = sectionNode as HTMLElement;
    if (sec.dataset.mzfLayoutFixed === '1') return; // idempotent — never double-shift

    const pr = sec.getBoundingClientRect();
    if (pr.width === 0) return; // not laid out yet — do NOT mark, so a later call can still fix it
    sec.dataset.mzfLayoutFixed = '1';
    const st = getComputedStyle(sec);
    const padL = parseFloat(st.paddingLeft) || 0;
    const padR = parseFloat(st.paddingRight) || 0;
    const padT = parseFloat(st.paddingTop) || 0;
    const padB = parseFloat(st.paddingBottom) || 0;
    const colW = sec.clientWidth - padL - padR; // text column width
    // margin (content) box in viewport coords
    const mL = pr.left + padL, mR = pr.right - padR, mT = pr.top + padT, mB = pr.bottom - padB;

    const addTransform = (el: HTMLElement, t: string) => {
      el.style.transform = el.style.transform ? `${el.style.transform} ${t}` : t;
    };

    // (pre) TIGHTEN THE HEADER/FOOTER BAND to its real inline content (letterhead / contact bar),
    // like Word. docx-preview nests each Word drawing in a block <div> INSIDE the run's <p> — invalid
    // HTML — so when the stored template is re-parsed via innerHTML (both the wizard preview and the
    // PDF host do this) the browser auto-closes the <p> at every <div> and leaves a trail of EMPTY
    // <p> fragments (each carrying docx-preview's `min-height:1em`). Those empty paragraphs, plus the
    // floating behind-text drawing's own in-flow block wrapper, inflate the band well past the
    // letterhead and push the whole body down (~45px of top drift on this letterhead). Word measures
    // the header from the inline drawing only. Removing the empty fragments' boxes and dropping the
    // behind-text drawings out of flow makes the band wrap just its image, so the body starts flush
    // beneath the letterhead — exactly where Word puts it. Header text (page numbers, contact lines)
    // is preserved: only boxes with neither text nor a drawing are collapsed.
    (Array.from(sec.querySelectorAll(':scope > header, :scope > footer')) as HTMLElement[]).forEach((band) => {
      Array.from(band.children).forEach((node) => {
        const el = node as HTMLElement;
        const empty = !el.querySelector('img, svg') && !(el.textContent || '').trim();
        if (empty && (el.tagName === 'P' || el.tagName === 'DIV')) el.style.display = 'none';
      });
      // behind-text drawings: keep them visible (absolute) but hoist their in-flow block wrappers
      // out of the box tree so they contribute no leading line to the band.
      (Array.from(band.querySelectorAll('svg, img')) as HTMLElement[]).forEach((d) => {
        const zi = getComputedStyle(d).zIndex;
        if (!zi || parseInt(zi, 10) >= 0) return;
        let w: HTMLElement | null = d.parentElement;
        while (w && w !== band) { if (getComputedStyle(w).display !== 'inline') w.style.display = 'contents'; w = w.parentElement; }
      });
    });

    // (a) behind-text watermarks: honor Word's mso-position:center on the margin box.
    (Array.from(sec.querySelectorAll('svg, img, div')) as HTMLElement[]).forEach((el) => {
      const zi = getComputedStyle(el).zIndex;
      if (!zi || parseInt(zi, 10) >= 0) return;
      const style = el.getAttribute('style') || '';
      const r = el.getBoundingClientRect();
      let dx = 0, dy = 0;
      if (/mso-position-horizontal:\s*center/.test(style)) dx = (mL + mR) / 2 - (r.left + r.right) / 2;
      if (/mso-position-vertical:\s*center/.test(style)) dy = (mT + mB) / 2 - (r.top + r.bottom) / 2;
      dx = Math.round(dx * 100) / 100;
      dy = Math.round(dy * 100) / 100;
      if (Math.abs(dx) < 1 && Math.abs(dy) < 1) return;
      addTransform(el, `translate(${dx}px, ${dy}px)`);
    });

    // (b) full-bleed header/footer bars: center on the page (they may bleed into the margins).
    (Array.from(sec.querySelectorAll(':scope > header, :scope > footer')) as HTMLElement[]).forEach((band) => {
      (Array.from(band.querySelectorAll('img, svg')) as HTMLElement[]).forEach((el) => {
        const zi = getComputedStyle(el).zIndex;
        if (zi && parseInt(zi, 10) < 0) return; // skip watermarks / behind-text drawings
        const r = el.getBoundingClientRect();
        if (r.width <= colW + 1) return; // narrow logo — leave it where Word put it
        const curCenter = (r.left + r.right) / 2 - pr.left;
        const dx = Math.round((pr.width / 2 - curCenter) * 100) / 100;
        if (Math.abs(dx) < 1) return;
        addTransform(el, `translateX(${dx}px)`);
      });
    });
  });
}
